import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Box, Button, Chip, InputAdornment, LinearProgress, Tab, Tabs, TextField, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import { useConfirm } from 'material-ui-confirm';

import { BackButton } from 'components';
import useOffShelfDishes from 'hooks/useOffShelfDishes';
import { getCurrencySymbol } from 'util/currencyUtils';
import { filterCategoryItems } from 'util/menuFilters';
import { countOffShelf, describeOffShelf } from 'util/offShelf';
import { getMenuCategories, getMenuGroups, getMyMenu, deleteMenuPhoto } from 'store/actions/restaurantMenu';
import MenuItemImage from 'views/MenuCategory/components/MenuItem/MenuItemImage';

import useMenuEditorActions from './useMenuEditorActions';
import { cookingCategoryNames, itemsWithDishOnTop, itemsWithoutDish, itemsInOrder } from './menuEditorPayloads';
import CategoryRail from './components/CategoryRail';
import DishesPane from './components/DishesPane';
import DishEditor from './components/DishEditor';
import CategoryEditor from './components/CategoryEditor';
import MoveDishDialog from './components/MoveDishDialog';
import GroupsPane from './components/GroupsPane';

const findDish = (categories, dishId) => {
  for (const category of categories) {
    const dish = (category.items || []).find((item) => item && item.id === dishId);
    if (dish) {
      return { dish, category };
    }
  }
  return { dish: null, category: null };
};

// The app theme forces every TextField to a 300px minimum width with fixed margins, and pads every
// outlined input with !important, which breaks side-by-side, small and multiline fields. The editor
// lays out its own fields, so it uses MUI's default text inputs.
const editorTheme = (outer) => ({
  ...outer,
  components: { ...outer.components, MuiTextField: undefined, MuiOutlinedInput: undefined },
});

const dishImage = (category, dish) => (category.images && category.images[dish.id]) || dish.image || null;

const MenuEditor = (props) => {
  const menuId = props.match.params.menuId;
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const actions = useMenuEditorActions(menuId);

  const categories = useSelector((state) => state.restaurantMenu.menuCategories);
  const groups = useSelector((state) => state.restaurantMenu.menuGroups);
  const storedMenu = useSelector((state) => state.restaurantMenu.menu);
  // The reducer keeps the first menu it loaded, so after opening another menu this can be stale.
  const menu = storedMenu && storedMenu.id === menuId ? storedMenu : null;
  const user = useSelector((state) => state.account.user);
  const isAdmin = !!(user && user.role === 'admin');
  const currencySymbol = getCurrencySymbol(user);
  const offShelfMap = useOffShelfDishes(user && user.restaurantId);
  const printCategoryNames = useMemo(() => cookingCategoryNames(menu), [menu]);

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dishes');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [query, setQuery] = useState('');
  const [offShelfOnly, setOffShelfOnly] = useState(false);
  const [showIds, setShowIds] = useState(false);
  const [editor, setEditor] = useState(null);
  const [imageFor, setImageFor] = useState(null);
  const [moving, setMoving] = useState(null);

  useEffect(() => {
    let current = true;
    setLoading(true);
    const loads = [dispatch(getMenuCategories(menuId)), dispatch(getMenuGroups(menuId))];
    if (storedMenu && storedMenu.id !== menuId) {
      loads.push(dispatch(getMyMenu(menuId)));
    }
    Promise.all(loads).finally(() => {
      if (current) {
        setLoading(false);
      }
    });
    return () => {
      current = false;
    };
    // Load once per menu; storedMenu is read only to decide whether the menu itself needs fetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, menuId]);

  const list = categories || [];
  const selectedCategory = list.find((category) => category.id === selectedCategoryId) || list[0] || null;

  const filtering = query.trim() !== '' || offShelfOnly;
  const filterOptions = { query, offShelfOnly, offShelfMap };

  const sections = useMemo(() => {
    const withEntries = (category) => ({
      category,
      entries: filtering
        ? filterCategoryItems(category.items, filterOptions)
        : (category.items || []).map((item, index) => ({ item, index })).filter((entry) => entry.item),
      offShelfCount: countOffShelf([category], offShelfMap),
    });
    if (filtering) {
      return list.map(withEntries).filter((section) => section.entries.length > 0);
    }
    return selectedCategory ? [withEntries(selectedCategory)] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, selectedCategory, filtering, query, offShelfOnly, offShelfMap]);

  const railCounts = useMemo(
    () =>
      Object.fromEntries(
        list.map((category) => [
          category.id,
          filtering ? filterCategoryItems(category.items, filterOptions).length : (category.items || []).filter(Boolean).length,
        ])
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, filtering, query, offShelfOnly, offShelfMap]
  );

  const dishCount = list.reduce((count, category) => count + (category.items || []).length, 0);
  const offShelfCount = countOffShelf(list, offShelfMap);

  // ---- dish actions shared by the list rows and the dish editor
  const deleteDish = async (dish, category) => {
    try {
      await confirm({ title: `Delete “${dish.name}”?`, confirmationText: 'Delete', confirmationButtonProps: { color: 'error' } });
    } catch (cancelled) {
      return false;
    }
    return actions.saveItems(itemsWithoutDish(category, dish.id, menuId));
  };

  const removeImage = async (dish, category) => {
    try {
      await confirm({ title: `Remove the photo of “${dish.name}”?`, confirmationText: 'Remove' });
    } catch (cancelled) {
      return;
    }
    await dispatch(deleteMenuPhoto({ menuId, categoryId: category.id, menuItemId: dish.id }));
  };

  const rowProps = (category, dish, index) => ({
    dish,
    image: dishImage(category, dish),
    offShelf: describeOffShelf(dish, offShelfMap),
    currencySymbol,
    showIds: isAdmin && showIds,
    groupCount: (dish.groups || []).length,
    canMoveToTop: !filtering && index > 0,
    onOpen: () => setEditor({ kind: 'dish', mode: 'edit', dishId: dish.id }),
    onUploadImage: () => setImageFor({ dishId: dish.id, categoryId: category.id }),
    onRemoveImage: () => removeImage(dish, category),
    onDuplicate: () => actions.duplicateDish({ ...dish, categoryId: category.id, menuId: dish.menuId || menuId }),
    onMove: () => setMoving({ ...dish, categoryId: category.id, menuId: dish.menuId || menuId }),
    onMoveToTop: () => actions.saveItems(itemsWithDishOnTop(category, index, menuId)),
    onDelete: () => deleteDish(dish, category),
  });

  // ---- editor drawers
  const editingDish = editor && editor.kind === 'dish' && editor.mode === 'edit' ? findDish(list, editor.dishId) : null;
  const newDishCategory = editor && editor.kind === 'dish' && editor.mode === 'new' ? list.find((c) => c.id === editor.categoryId) : null;
  const dishCategory = editingDish ? editingDish.category : newDishCategory;
  const editingCategory = editor && editor.kind === 'category' && editor.mode === 'edit' ? list.find((c) => c.id === editor.categoryId) || null : null;

  return (
    <ThemeProvider theme={editorTheme}>
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          height: { md: 'calc(100vh - 64px)' },
          minHeight: 0,
          bgcolor: 'background.default',
        }}
      >
        <BackButton to={`/menu/${menuId}`} label="Menu" />
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 1, mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 600 }}>
              {(menu && menu.menuName) || 'Menu editor'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {list.length} categories · {dishCount} dishes{offShelfCount > 0 ? ` · ${offShelfCount} off shelf` : ''}
              {isAdmin && showIds ? ` · Menu ID ${menuId}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {isAdmin && (
              <Button size="small" color="inherit" onClick={() => setShowIds(!showIds)}>
                {showIds ? 'Hide IDs' : 'Show IDs'}
              </Button>
            )}
            <Button size="small" component={RouterLink} to={`/menuCategory/${menuId}`}>
              Open classic editor
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={(event, value) => setTab(value)} sx={{ flex: 1, minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 500, fontSize: 15 } }}>
            <Tab value="dishes" label="Dishes" />
            <Tab value="choices" label={`Choices & add-ons (${(groups || []).length})`} />
          </Tabs>
          {/* Hidden rather than removed on the other tab, so the tabs keep their width and indicator. */}
          <Box
            sx={{ display: 'flex', gap: 1, alignItems: 'center', pb: 1, width: { xs: '100%', sm: 'auto' }, visibility: tab === 'dishes' ? 'visible' : 'hidden' }}
            aria-hidden={tab !== 'dishes'}
          >
              <TextField
                size="small"
                placeholder="Search dishes"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                inputProps={{ 'aria-label': 'Search dishes' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: query ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => setQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ flex: 1, minWidth: 220 }}
              />
              <Chip
                label="Off shelf only"
                clickable
                color={offShelfOnly ? 'primary' : 'default'}
                variant={offShelfOnly ? 'filled' : 'outlined'}
                onClick={() => setOffShelfOnly(!offShelfOnly)}
                aria-pressed={offShelfOnly}
              />
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Box sx={{ flex: 1, minHeight: 0 }}>
          {tab === 'dishes' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gridTemplateRows: { xs: 'auto 1fr', md: '1fr' }, gap: 2, height: '100%', minHeight: 0 }}>
              <Box sx={{ minHeight: 0, maxHeight: { xs: 280, md: 'none' } }}>
                <CategoryRail
                  categories={list}
                  selectedId={selectedCategory ? selectedCategory.id : null}
                  onSelect={(id) => {
                    setSelectedCategoryId(id);
                    if (filtering) {
                      setQuery('');
                      setOffShelfOnly(false);
                    }
                  }}
                  onReorder={(from, to) => actions.reorderCategories(list, from, to)}
                  onAdd={() => setEditor({ kind: 'category', mode: 'new' })}
                  counts={railCounts}
                  filtering={filtering}
                />
              </Box>
              <Box sx={{ minHeight: 0 }}>
                <DishesPane
                  sections={sections}
                  filtering={filtering}
                  renderRowProps={rowProps}
                  onReorderDishes={(category, from, to) => actions.saveItems(itemsInOrder(category, from, to, menuId))}
                  onAddDish={(categoryId) => setEditor({ kind: 'dish', mode: 'new', categoryId })}
                  onEditCategory={(categoryId) => setEditor({ kind: 'category', mode: 'edit', categoryId })}
                  showIds={isAdmin && showIds}
                  emptyMessage={
                    loading
                      ? 'Loading the menu…'
                      : filtering
                        ? offShelfOnly && !query.trim()
                          ? 'Every dish is on the menu.'
                          : 'No dishes match.'
                        : 'Add a category to start building this menu.'
                  }
                />
              </Box>
            </Box>
          ) : (
            <GroupsPane groups={groups || []} categories={list} menuId={menuId} currencySymbol={currencySymbol} showIds={isAdmin && showIds} actions={actions} />
          )}
        </Box>
      </Box>

      <DishEditor
        open={!!(editor && editor.kind === 'dish')}
        mode={editor && editor.kind === 'dish' ? editor.mode : 'new'}
        dish={editingDish ? editingDish.dish : null}
        category={dishCategory}
        menuId={menuId}
        groups={groups || []}
        menu={menu}
        printCategoryNames={printCategoryNames}
        currencySymbol={currencySymbol}
        image={editingDish && editingDish.dish ? dishImage(editingDish.category, editingDish.dish) : null}
        actions={actions}
        onClose={() => setEditor(null)}
        onCreated={(id, categoryId) => {
          setSelectedCategoryId(categoryId);
          // Carry on editing the new dish (photo, choices, …) when the server returned its id.
          setEditor(typeof id === 'string' ? { kind: 'dish', mode: 'edit', dishId: id } : null);
        }}
        onUploadImage={() => editingDish && editingDish.dish && setImageFor({ dishId: editingDish.dish.id, categoryId: editingDish.category.id })}
        onDuplicate={() => editingDish && editingDish.dish && actions.duplicateDish({ ...editingDish.dish, categoryId: editingDish.category.id, menuId: editingDish.dish.menuId || menuId })}
        onMove={() => editingDish && editingDish.dish && setMoving({ ...editingDish.dish, categoryId: editingDish.category.id, menuId: editingDish.dish.menuId || menuId })}
        onDelete={async () => {
          if (editingDish && editingDish.dish && (await deleteDish(editingDish.dish, editingDish.category))) {
            setEditor(null);
          }
        }}
      />

      <CategoryEditor
        open={!!(editor && editor.kind === 'category')}
        mode={editor && editor.kind === 'category' ? editor.mode : 'new'}
        category={editingCategory}
        menuId={menuId}
        menu={menu}
        printCategoryNames={printCategoryNames}
        showIds={isAdmin && showIds}
        actions={actions}
        onClose={() => setEditor(null)}
        onDeleted={() => {
          setEditor(null);
          setSelectedCategoryId(null);
        }}
      />

      <MoveDishDialog
        open={!!moving}
        dish={moving}
        categories={list}
        onClose={() => setMoving(null)}
        onMove={(dish, targetId) => actions.moveDish(dish, targetId)}
      />

      {imageFor && (
        <MenuItemImage
          open
          handImageClose={() => setImageFor(null)}
          menuId={menuId}
          categoryId={imageFor.categoryId}
          menuItemId={imageFor.dishId}
        />
      )}
    </DndProvider>
    </ThemeProvider>
  );
};

MenuEditor.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ menuId: PropTypes.string }) }).isRequired,
};

export default MenuEditor;
